import React, { FC, useEffect, useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import { useGetPhotosQuery } from "@store/photoApi";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { Photo } from "@models/photo";
import "swiper/css";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import Avatar from "@components/Avatar/Avatar";
import { useGetAlbumPhotosQuery } from "@store/albumApi";
import ImageGallery, { ReactImageGalleryItem } from "react-image-gallery";
import "./image-gallery.css";
import StateIcon from "@components/StateIcon/StateIcon";
import Subtitles from "@components/Subtitles";
import { ScreenSize } from "@constants/screen-size";

const AvatarContainer = styled.div`
  position: absolute;
  bottom: 96px;
  right: 4px;
  width: 20%;
  height: 25%;
  min-width: 120px;
  min-height: 90px;
  max-width: 300px;
  max-height: 300px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
`;

const State = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: calc(100dvh - 206px);

  @media screen and (orientation: landscape) {
    top: calc(100dvh - 132px);
  }
`;

const SubtitlesContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-60%);
  display: flex;
  justify-content: center;
  overflow: hidden;
  text-overflow: ellipsis;
  bottom: 160px;
  width: 70%;
  max-height: 50dvh;

  @media (max-width: ${ScreenSize.md}px) {
    transform: translateX(-70%);
  }

  @media (max-width: ${ScreenSize.sm}px) {
    transform: translateX(-75%);
    width: 65%;
  }

  @media (orientation: landscape) {
    bottom: 145px;
  }
`;

const PhotosCompanion: FC<{
  albumId?: string;
  sessionId: string;
  companionName: string;
}> = ({ albumId, sessionId, companionName }) => {
  const { t } = useTranslation();
  const { ready } = useSelector((state: RootState) => state.avatar);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const { text, subtitlesOn } = useSelector((state: RootState) => state.avatar);
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: allPhotos, isLoading: isAllPhotosLoading } = useGetPhotosQuery(
    { patientId: selectedPatientId! },
    {
      skip: !selectedPatientId || !!albumId,
    },
  );
  const { data: albumPhotos, isLoading: isAlbumPhotosLoading } =
    useGetAlbumPhotosQuery(
      { albumId: albumId! },
      {
        skip: !albumId,
      },
    );

  const photos = useMemo(() => {
    return (albumId ? albumPhotos : allPhotos)?.data || [];
  }, [albumId, albumPhotos, allPhotos]);

  const galleryItems: ReactImageGalleryItem[] = useMemo(() => {
    return photos.map((photo) => ({
      original: photo.fileUrl,
      thumbnail: photo.fileUrl,
    }));
  }, [photos]);

  const isLoading = useMemo(() => {
    return albumId ? isAlbumPhotosLoading : isAllPhotosLoading;
  }, [albumId, isAlbumPhotosLoading, isAllPhotosLoading]);

  useEffect(() => {
    if (photos?.length) {
      setSelectedPhoto(photos[0]);
    } else {
      setSelectedPhoto(null);
    }
  }, [photos]);

  if (isLoading) {
    return null;
  }

  return (
    <Box position="relative">
      {ready && (
        <>
          {galleryItems.length ? (
            <ImageGallery
              items={galleryItems}
              onSlide={(index) => setSelectedPhoto(photos[index])}
              showPlayButton={false}
              showFullscreenButton={false}
              additionalClass="image-gallery"
            />
          ) : (
            <Box display="flex" justifyContent="center">
              <Alert severity="warning">{t("photosCompanion.noPhotos")}</Alert>
            </Box>
          )}
        </>
      )}

      {subtitlesOn && text && (
        <SubtitlesContainer>
          <Subtitles text={text} />
        </SubtitlesContainer>
      )}
      <State>
        <StateIcon />
      </State>

      <AvatarContainer>
        <Avatar
          sessionId={sessionId}
          photoId={selectedPhoto?.id}
          companionName={companionName}
        />
      </AvatarContainer>
    </Box>
  );
};
export default PhotosCompanion;
